import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'

import DetailPageTemplate from '../../templates/DetailPageTemplate'
import adjustColorContrast from '../../utilities/adjustColorContrast'
import { buildCorrectedGradient } from '../../utilities/paletteColors'
import fetchWithAuth from '../../utilities/fetchWithAuth'

const PLOT_TYPE_LABELS = {
  'time-series': 'Time Series',
  histogram: 'Histogram',
}

const PALETTE_OPTIONS = [
  { value: 'rainbow', label: 'Rainbow' },
  { value: 'magma', label: 'Magma' },
  { value: 'viridis', label: 'Viridis' },
  { value: 'plasma', label: 'Plasma' },
  { value: 'spectral', label: 'Spectral' },
]

const FieldRow = ({ label, children }) => (
  <Box display="flex" alignItems="flex-start" gap={2}>
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{ minWidth: 180, flexShrink: 0 }}
    >
      {label}
    </Typography>
    <Box>{children}</Box>
  </Box>
)

const PlotPage = () => {
  const { uuid } = useParams()
  const navigate = useNavigate()
  const theme = useTheme()
  const [plot, setPlot] = useState(null)
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  useEffect(() => {
    fetchWithAuth(`plots/${uuid}`)
      .then(({ data }) => {
        if (data?.data) {
          setPlot(data.data)
        }
      })
      .catch((error) => console.error('Error fetching plot:', error))
  }, [uuid])

  const handleDelete = async () => {
    try {
      await fetchWithAuth(`plots/${uuid}`, { method: 'DELETE' })
      navigate('/plot-config')
    } catch (error) {
      console.error('Error deleting plot:', error)
    }
  }

  if (!plot) {
    return <DetailPageTemplate isLoading={true} />
  }

  const parameters = plot.config?.parameters ?? []

  const detailsSection = {
    title: 'Plot Details',
    content: (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.5}>
            <FieldRow label="Name">
              <Typography>{plot.plotName || '-'}</Typography>
            </FieldRow>
            <FieldRow label="Analysis">
              {plot.analysisName ? (
                <Link to={`/analyses/${plot.analysis}`}>
                  {plot.analysisName}
                </Link>
              ) : (
                <Typography>{plot.analysis || '-'}</Typography>
              )}
            </FieldRow>
            <FieldRow label="Type">
              <Typography>
                {PLOT_TYPE_LABELS[plot.type] || plot.type || '-'}
              </Typography>
            </FieldRow>
            <FieldRow label="Unit">
              <Typography>{plot.unit || '-'}</Typography>
            </FieldRow>
            <FieldRow label="Active">
              <Typography>{plot.active ? 'Yes' : 'No'}</Typography>
            </FieldRow>
            {plot.type !== 'histogram' && (
              <FieldRow label="Exclude Background">
                <Typography>{plot.excludeBackground ? 'Yes' : 'No'}</Typography>
              </FieldRow>
            )}
            {plot.type === 'time-series' && (
              <FieldRow label="Multiline">
                <Typography>{plot.isMultiline ? 'Yes' : 'No'}</Typography>
              </FieldRow>
            )}
          </Stack>
        </CardContent>
      </Card>
    ),
  }

  const colorSection = {
    title: 'Color',
    content: (
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2.5}>
            {plot.isMultiline && plot.type === 'time-series' ? (
              <FieldRow label="Color Palette">
                {(() => {
                  const p = PALETTE_OPTIONS.find(
                    (o) => o.value === plot.colorPalette,
                  )
                  return p ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 12,
                          borderRadius: 0.5,
                          background: buildCorrectedGradient(p.value, theme),
                          flexShrink: 0,
                        }}
                      />
                      <Typography>{p.label}</Typography>
                    </Box>
                  ) : (
                    <Typography>{plot.colorPalette || '-'}</Typography>
                  )
                })()}
              </FieldRow>
            ) : (
              <FieldRow label="Color">
                {plot.color ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: 0.5,
                        bgcolor: adjustColorContrast(plot.color, theme),
                        flexShrink: 0,
                      }}
                    />
                    <Typography>{plot.color}</Typography>
                  </Box>
                ) : (
                  <Typography>-</Typography>
                )}
              </FieldRow>
            )}
          </Stack>
        </CardContent>
      </Card>
    ),
  }

  const parametersSection = {
    title: 'Parameters',
    content: (
      <Card variant="outlined">
        <CardContent>
          {parameters.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              textAlign="center"
            >
              No parameters configured
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {parameters.map((p) => (
                <Chip key={p} label={p} size="small" />
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    ),
  }

  return (
    <>
      <DetailPageTemplate
        title={plot.plotName || 'Plot'}
        breadcrumbs={['plot-config', plot.plotName]}
        actions={
          <Stack direction="row" spacing={2}>
            <Button
              component={Link}
              to={`/plot-config/${uuid}/edit`}
              variant="contained"
              color="primary"
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              onClick={() => setOpenDeleteDialog(true)}
            >
              Delete
            </Button>
          </Stack>
        }
        sections={[detailsSection, colorSection, parametersSection]}
        dialogs={
          <Dialog
            open={openDeleteDialog}
            onClose={() => setOpenDeleteDialog(false)}
          >
            <DialogTitle>Delete Plot</DialogTitle>
            <DialogContent>
              <DialogContentText>
                Are you sure you want to delete &quot;{plot.plotName}&quot;?
                This action cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
              <Button onClick={handleDelete} color="error" autoFocus>
                Delete
              </Button>
            </DialogActions>
          </Dialog>
        }
      />
    </>
  )
}

export default PlotPage
